import { parseOrderTimestamp } from "./salesOrderReceiptUtils";

function toBool(value) {
  return value === true || value === "true" || value === 1 || value === "1";
}

/** True when an order has a usable id (including 0). */
export function hasOrderId(order) {
  const id = order?.orderId ?? order?.id;
  return id !== null && id !== undefined && id !== "";
}

/** Stable string key for order maps (0 → "0"). */
export function getOrderKey(order) {
  if (!hasOrderId(order)) return null;
  return String(order.orderId ?? order.id);
}

/** Normalize list/single-object API responses. */
export function coalesceOrdersResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return [data];
  return [];
}

/** Human-readable order number for UI (falls back to orderId). */
export function getOrderDisplayNumber(order) {
  if (!order) return "—";
  return (
    order.orderNumber ||
    order.orderId ||
    order.POID ||
    order.id ||
    "—"
  );
}

/** Parse order createdAt / orderDate for reports and dashboards. */
export function getOrderCreatedAt(order) {
  const raw = order?.orderDate || order?.createdAt;
  return parseOrderTimestamp(raw);
}

export function isDraftOrder(order) {
  return toBool(order?.draft);
}

/** Till tickets stamped by HPOS checkout (externalId HPOS). */
export function isHposOriginOrder(order) {
  if (!order) return false;
  const candidates = [
    order.externalId,
    order.ExternalId,
    order.external_id,
    order.sourceApp,
    order.source_app,
    order.origin,
    order.channel,
    order.productOrder?.externalId,
    order.productOrder?.ExternalId,
  ];
  for (const raw of candidates) {
    const ext = String(raw || "").trim().toUpperCase();
    if (ext === "HPOS" || ext.startsWith("HPOS:") || ext.startsWith("HPOS-")) {
      return true;
    }
  }
  return false;
}

export function excludeHposOriginOrders(list) {
  return (Array.isArray(list) ? list : []).filter((order) => !isHposOriginOrder(order));
}

export function isReturnedOrder(order) {
  return toBool(order?.returned);
}

/** Non-draft, non-returned sales orders (shown on dashboards). */
export function isActiveSalesOrder(order) {
  return !isDraftOrder(order) && !isReturnedOrder(order);
}

export function isOrderInDateRange(order, start, end) {
  if (!start || !end) return false;
  const orderDate = getOrderCreatedAt(order);
  if (Number.isNaN(orderDate.getTime())) return false;
  return orderDate >= start && orderDate <= end;
}

function derivePaid(order = {}) {
  if (toBool(order.paid)) return true;

  const paymentLabel = String(
    order.payment || order.paymentStatus || ""
  ).toLowerCase();
  if (paymentLabel === "paid") return true;

  const billStatus = String(order.billStatus || "").toLowerCase();
  if (billStatus === "paid") return true;

  const payable = Number(order.payableAmount ?? order.total ?? 0);
  const received = Number(order.amountReceived ?? 0);
  if (payable > 0 && received >= payable) return true;

  return false;
}

function deriveFulfilled(order = {}) {
  if (toBool(order.fulfilled)) return true;

  const status = String(order.status || "").toLowerCase();
  if (status === "closed" || status === "fulfilled") return true;

  const billStatus = String(order.billStatus || "").toLowerCase();
  if (billStatus === "closed" || billStatus === "fulfilled") return true;

  if (Array.isArray(order.lineItems) && order.lineItems.length > 0) {
    return order.lineItems.every((item) => toBool(item.fulfilled));
  }

  return false;
}

/** Normalize API order fields so list/detail UI stay in sync. */
export function normalizeOrderRecord(order, overrides = {}) {
  if (!order) return order;

  const merged = { ...order, ...overrides };

  const paid = derivePaid(merged);
  const fulfilled = deriveFulfilled(merged);
  const cancelled =
    String(merged.status || "").toLowerCase() === "cancelled" ||
    String(merged.status || "").toLowerCase() === "canceled" ||
    String(merged.state || "").toLowerCase() === "cancelled" ||
    String(merged.state || "").toLowerCase() === "canceled";
  const draft = toBool(merged.draft) || String(merged.status || "").toLowerCase() === "draft";
  const returned =
    toBool(merged.returned) ||
    toBool(merged.fullyReturned) ||
    toBool(merged.partiallyReturned) ||
    String(merged.status || "").toLowerCase() === "returned" ||
    String(merged.state || "").toLowerCase() === "returned";

  // Backend closes when paid && fulfilled; keep Redux/UI in sync without waiting for a full list refresh.
  let status = merged.status;
  let close = merged.close;
  if (!cancelled && !draft && !returned && paid && fulfilled) {
    status = "closed";
    close = true;
  } else if (!status && fulfilled) {
    status = "closed";
  }

  const lines = Array.isArray(merged.lineItems) ? merged.lineItems : [];
  const lineSubtotal = lines.reduce((sum, li) => {
    const qty = Number(li.quantity) || 0;
    const price = Number(li.price ?? li.unitPrice ?? li.salePrice) || 0;
    return sum + qty * price;
  }, 0);
  const subTotal =
    Number(merged.subTotal ?? merged.subtotal) > 0
      ? Number(merged.subTotal ?? merged.subtotal)
      : lineSubtotal || Number(merged.subTotal ?? merged.subtotal) || 0;

  return {
    ...merged,
    orderId: hasOrderId(merged) ? String(merged.orderId ?? merged.id) : merged.orderId,
    paid,
    fulfilled,
    draft,
    returned: toBool(merged.returned) || toBool(merged.fullyReturned) || toBool(merged.partiallyReturned),
    fullyReturned: toBool(merged.fullyReturned),
    partiallyReturned: toBool(merged.partiallyReturned),
    paymentType: merged.paymentType || merged.paymentMethod || null,
    payableAmount: merged.payableAmount ?? merged.total ?? 0,
    subTotal,
    subtotal: subTotal,
    status,
    close: close === true || status === "closed",
  };
}

function mergeOrderRecord(existing, incoming) {
  const incomingNorm = normalizeOrderRecord(incoming);
  if (!existing) return incomingNorm;

  const existingNorm = normalizeOrderRecord(existing);
  const existingCancelled =
    String(existingNorm.status || "").toLowerCase() === "cancelled" ||
    String(existingNorm.status || "").toLowerCase() === "canceled" ||
    String(existingNorm.state || "").toLowerCase() === "cancelled" ||
    String(existingNorm.state || "").toLowerCase() === "canceled";
  const incomingCancelled =
    String(incomingNorm.status || "").toLowerCase() === "cancelled" ||
    String(incomingNorm.status || "").toLowerCase() === "canceled" ||
    String(incomingNorm.state || "").toLowerCase() === "cancelled" ||
    String(incomingNorm.state || "").toLowerCase() === "canceled";

  const merged = {
    ...existingNorm,
    ...incomingNorm,
    paid: incomingNorm.paid || existingNorm.paid,
    fulfilled: incomingNorm.fulfilled || existingNorm.fulfilled,
    paymentType: incomingNorm.paymentType || existingNorm.paymentType,
    amountReceived: incomingNorm.amountReceived ?? existingNorm.amountReceived,
    payableAmount: incomingNorm.payableAmount ?? existingNorm.payableAmount,
  };

  // Keep cancelled once set — list APIs sometimes omit status and would otherwise reopen the order.
  if (existingCancelled && !incomingCancelled) {
    merged.status = existingNorm.status || "cancelled";
    merged.state = existingNorm.state || existingNorm.status || "cancelled";
  }

  return normalizeOrderRecord(merged);
}

export function mergeOrderList(currentOrders = [], incomingOrders = []) {
  const byId = new Map(
    currentOrders.map((order) => [getOrderKey(order), normalizeOrderRecord(order)]).filter(([key]) => key != null)
  );

  incomingOrders.forEach((order) => {
    const key = getOrderKey(order);
    if (key == null) return;
    byId.set(key, mergeOrderRecord(byId.get(key), order));
  });

  return Array.from(byId.values());
}

export function extractOrderFromApiResponse(response) {
  if (Array.isArray(response)) return response[0] || null;
  return response || null;
}

/** POST /orders often echoes orderId 0 — treat as needing server resolution. */
export function isUnresolvedOrderId(orderId) {
  if (orderId === null || orderId === undefined || orderId === "") return true;
  const numeric = Number(orderId);
  return !Number.isNaN(numeric) && numeric === 0;
}

/** Next sequential order id (POS pattern: max existing + 1). */
export function getNextOrderId(existingOrders = []) {
  let max = 0;
  for (const order of existingOrders) {
    const numeric = Number(order?.orderId ?? order?.id);
    if (!Number.isNaN(numeric) && numeric > max) {
      max = numeric;
    }
  }
  return String(max + 1);
}

/**
 * After POST /orders, resolve the persisted order id from GET /orders when the
 * create response returns orderId 0 or omits it. (Match production main.)
 */
function ordersRoughlyMatch(candidate, target) {
  const totalA = Number(candidate?.payableAmount ?? candidate?.total ?? 0);
  const totalB = Number(target?.payableAmount ?? target?.total ?? 0);
  if (Math.abs(totalA - totalB) > 0.02) return false;

  const itemsA = (candidate?.lineItems || []).length;
  const itemsB = (target?.lineItems || []).length;
  if (itemsA !== itemsB) return false;

  const custA = candidate?.customerId;
  const custB = target?.customerId;
  if (
    custA != null &&
    custB != null &&
    String(custA) !== "null" &&
    String(custB) !== "null" &&
    String(custA) !== String(custB)
  ) {
    return false;
  }

  return true;
}

function pickNewestOrder(orders = []) {
  return [...orders].sort(
    (a, b) => getOrderCreatedAt(b).getTime() - getOrderCreatedAt(a).getTime()
  )[0];
}

function mergeResolvedOrder(postResult, createPayload, match) {
  return normalizeOrderRecord({
    ...createPayload,
    ...postResult,
    ...match,
    lineItems: match?.lineItems?.length
      ? match.lineItems
      : postResult?.lineItems?.length
        ? postResult.lineItems
        : createPayload?.lineItems,
  });
}

const RESOLVE_ORDER_RETRY_MS = [0, 400, 1200];

export async function resolveOrderAfterCreate(apiClient, postResult, createPayload) {
  const merged = normalizeOrderRecord({
    ...createPayload,
    ...postResult,
    lineItems: postResult?.lineItems?.length
      ? postResult.lineItems
      : createPayload?.lineItems,
  });

  if (!isUnresolvedOrderId(merged.orderId)) {
    return merged;
  }

  const target = { ...createPayload, ...postResult };

  for (const delayMs of RESOLVE_ORDER_RETRY_MS) {
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    try {
      const list = coalesceOrdersResponse(
        await apiClient.get("/orders?start=0&size=200", {
          silent: true,
          context: "Resolving created order",
        })
      );

      const matches = list
        .filter((order) => ordersRoughlyMatch(order, target))
        .sort(
          (a, b) => getOrderCreatedAt(b).getTime() - getOrderCreatedAt(a).getTime()
        );

      if (matches.length > 0 && !isUnresolvedOrderId(matches[0].orderId)) {
        return mergeResolvedOrder(postResult, createPayload, matches[0]);
      }

      const newest = pickNewestOrder(list);
      if (
        newest &&
        !isUnresolvedOrderId(newest.orderId) &&
        ordersRoughlyMatch(newest, target)
      ) {
        return mergeResolvedOrder(postResult, createPayload, newest);
      }
    } catch {
      // retry
    }
  }

  if (createPayload?.orderId && !isUnresolvedOrderId(createPayload.orderId)) {
    return normalizeOrderRecord({
      ...merged,
      orderId: String(createPayload.orderId),
    });
  }

  return merged;
}
